<template>
  <div>
    <table class="table table-sm mb-0">
      <tr v-if="showThumbnail">
        <td class="border-top-0 font-weight-bold">{{ $t('Image') }}</td>
        <td class="border-top-0">
          <img class="thumbnail" v-if="src" :src="src"  alt="Lot Image"/>
        </td>
      </tr>
      <tr>
        <td :class="showThumbnail ? '' : 'border-top-0'" class="font-weight-bold">{{ $t('Lot') }}</td>
        <td :class="showThumbnail ? '' : 'border-top-0'">{{ reservation.lot_no ? reservation.lot_no : $t('N/A') }}</td>
      </tr>
      <tr v-if="reservation.lot">
        <td class="font-weight-bold">{{ $t('Lot UUID') }}</td>
        <td>
          {{ reservation.lot.uuid }}
          <b-tooltip :target="`copy-lot-uuid`" :title="copyTooltip"/>
          <font-awesome-icon
            id="copy-lot-uuid"
            icon="copy"
            class="ml-2 copy-icon"
            @click="copyLotUuid"
          />
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Receipt / Item') }}</td>
        <td>
          <router-link v-if="$hasPermission('view_receipt')"
                       :to="{name: 'receipt.view', params: {id: inventory.receipt_uuid}}">
            {{ inventory.receipt.receipt_no }}
          </router-link>
          <p class="m-0" v-else> {{ inventory.receipt.receipt_no }}</p>
          /
          <router-link v-if="$hasPermission('view_inventoryitem')"
                       :to="{ name: 'inventory.view', params: { id: inventory.uuid }}">
            {{ inventory.item_no }}
          </router-link>
          <grouped-item-indicator :inventory="inventory"/>
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Lot Title') }}</td>
        <td>{{ inventory.short_description | striptags }}</td>
      </tr>
      <tr v-if="showDescription">
        <td class="font-weight-bold">{{ $t('Lot Description') }}</td>
        <td>{{ inventory.dynamic_fields | attributeByMasterLanguage('description') | striptags | truncate(200) }}</td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Sale') }}</td>
        <td>
          <div>
            <router-link v-if="!enableContinuousAuction"
                         :to="{name: 'sales.view', params: {id: reservation.auction.uuid}}">
              {{ reservation.auction.sale_no }}
            </router-link>
            <span v-else>
                              {{ reservation.auction.sale_no }}
                            </span>
            <live-sale-ref v-if="reservation.auction.live_reference"
                           :href="reservation.auction.live_url"
                           :label="reservation.auction.live_reference"/>
          </div>
        </td>
      </tr>
      <tr v-if="enableInventoryQuantity">
        <td class="font-weight-bold">{{ $t('Quantity') }}</td>
        <td>{{ reservation.quantity }}</td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Status') }}</td>
        <td>
          <template>
            <template>
              <router-link v-if="!enableContinuousAuction"
                           :key="`${reservation.uuid}-${reservation.auction.uuid}`"
                           :to="{name: 'sales.view', params: {id: reservation.auction.uuid}}">
                                <span :key="reservation.uuid" class="mr-1"
                                      v-as-status="reservation.status">
                                  {{ reservation.auction.sale_no }} -
                                  {{ reservation.status | removeUnderscores | toUpperCaseFirst }}
                                </span>
              </router-link>
              <span v-else :key="reservation.uuid" class="mr-1"
                    v-as-status="reservation.status">
                                  {{ reservation.auction.sale_no }} -
                                  {{ reservation.status | removeUnderscores | toUpperCaseFirst }}
                                </span>
            </template>
          </template>
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Receive Date') }}</td>
        <td>{{ inventory.receive_date | toDate }}</td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Collected') }}</td>
        <td>
          <span v-if="reservation.collected" class="badge badge-success">{{ $t('Yes') }}</span>
          <span v-else class="badge badge-danger">{{ $t('No') }}</span>
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Branch') }}</td>
        <td>
          <template v-if="inventory.branch">
            <router-link v-if="$hasPermission('setup')"
                         :to="{name: 'branches', params: {branch_uuid: inventory.branch_uuid}}">
              {{ inventory.branch.name }}
            </router-link>
            <span v-else>{{ inventory.branch.name }}</span>
          </template>
        </td>
      </tr>
      <tr v-if="!inventory.is_grouped_item">
        <td class="font-weight-bold">{{ $t('Department') }}</td>
        <td>
          <template v-if="inventory.department">
            <router-link v-if="$hasPermission('setup')"
                         :to="{name: 'departments', params: {department_uuid: inventory.department_uuid}}">
              {{ inventory.department.name | masterLanguage }}
            </router-link>
            <span v-else>{{ inventory.department.name | masterLanguage }}</span>
          </template>
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Specialists') }}</td>
        <td>
          <ul v-if="inventory.specialists" style="list-style: none; padding-left: 0;">
            <li v-for="specialist in inventory.specialists" :key="specialist.uuid">
                              <span>
                              {{ specialist.display_name }} - {{ specialist.email }}
                              </span>
            </li>
          </ul>
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Tax Status') }}</td>
        <td>{{ taxStatusName }}</td>
      </tr>
      <tr v-if="!hideFields.includes('commission_tiers')">
        <td class="font-weight-bold">{{ $t('Commissions') }}</td>
        <td>
          <table v-if="commissionTiers && commissionTiers.length > 0" class="info-table table table-sm mb-0">
            <tr>
              <th class="border-top-0">{{$t('Percentage')}}</th>
              <th class="border-top-0" v-if="commissionTiers.length > 1">{{$t('Over')}}</th>
            </tr>
            <tr v-for="(commission, index) in commissionTiers" :key="index">
              <td>{{commission.percent}} %</td>
              <td v-if="commissionTiers.length > 1">{{(commission.amount_over || 0) | asCurrency}}</td>
            </tr>
          </table>
        </td>
      </tr>
      <tr v-if="!hideFields.includes('minimum_commission')">
        <td class="font-weight-bold">{{ $t('Minimum Commission') }}</td>
        <td>{{ minimumCommission | asCurrency }}</td>
      </tr>
      <tr v-if="!inventory.is_grouped_item">
        <td class="font-weight-bold">{{ $t('Category') }}</td>
        <td><span v-if="inventory.category">{{ inventory.category.name | masterLanguage }}</span></td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Item Location') }}</td>
        <td><span v-if="inventory.item_location_uuid">{{ itemLocationComputedName }}</span></td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Estimate') }}</td>
        <td>
          <template v-if="reservation.low || reservation.high">
            {{ reservation.low | asCurrency }} - {{ reservation.high | asCurrency }}
          </template>
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Start Price') }}</td>
        <td>
          {{ reservation.start_price | asCurrency }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Reserve') }}</td>
        <td>
          {{ reservation.reserve | asCurrency }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Reserve Type') }}</td>
        <td>
          {{ displayReserveType }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">{{ $t('Collection Information') }}</td>
        <td>
          {{ inventory.collection_info_full }}
        </td>
      </tr>
    </table>

  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import LiveSaleRef from "@/core/components/common/LiveSaleRef";
  import GroupedItemIndicator from "@/modules/inventory/GroupedItemIndicator";
  import ItemLocationService from "@/modules/setup/branch/ItemLocationService";

  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "ReservationInfoTable",
    components: {GroupedItemIndicator, LiveSaleRef},
    props: {
      reservation: {
        type: Object,
        required: true
      },
      showThumbnail: {
        type: Boolean,
        default: false
      },
      showDescription: {
        type: Boolean,
        default: false
      },
      hideFields: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        copyTooltip: this.$t('Copy to clipboard')
      }
    },
    methods: {
      async copyLotUuid() {
        try {
          await navigator.clipboard.writeText(this.reservation.lot.uuid);
          this.copyTooltip = this.$t('Copied!');
        } catch (err) {
          this.copyTooltip = this.$t('Error copying.');
        }
        setTimeout(() => {
          this.copyTooltip = this.$t('Copy to clipboard');
        }, 2000);
      }
    },
    computed: {
      ...globalMapState({
        taxStatuses: state => state.taxStatuses,
        enableInventoryQuantity: state => state.enableInventoryQuantity,
        enableContinuousAuction: state => state.enableContinuousAuction,
        reserveTypes: state => state.reserveTypes,
        itemLocations: state => state.itemLocations,
      }),
      inventory() {
        return this.reservation.inventory
      },
      itemLocationComputedName() {
        if (!this.inventory || !this.inventory.item_location_uuid) {
          return '';
        }
        if (this.itemLocations && this.itemLocations.length) {
          const node = ItemLocationService.findNode(this.itemLocations, this.inventory.item_location_uuid, 'uuid', 'children');
          if (node) return node.computed_name;
        }
        return this.inventory.item_location ? this.inventory.item_location.name : '';
      },
      displayReserveType() {
        if (!this.reservation || !this.reservation.reserve_type) {
          return ''
        }
        const tmpType = this.reserveTypes.find(r => r.value === this.reservation.reserve_type)
        return tmpType ? tmpType.key : ""
      },
      taxStatusName() {
        if (!this.taxStatuses) {
          return ''
        }

        let taxStatusUuid = null;

        if (this.reservation.lot && this.reservation.lot.financial_item) {
          taxStatusUuid = this.reservation.lot.financial_item.tax_status_uuid
        } else if (this.inventory) {
          taxStatusUuid = this.inventory.tax_status_uuid
        }

        const taxStatus = this.taxStatuses.find(ts => ts.uuid === taxStatusUuid);
        return taxStatus ? taxStatus.name : '';
      },
      minimumCommission() {
        if (this.reservation.lot && this.reservation.lot.financial_item) {
          return this.reservation.lot.financial_item.minimum_commission
        } else if (this.inventory) {
          return this.inventory.minimum_commission
        }
        return '';
      },
      commissionTiers() {
        if (this.reservation.lot && this.reservation.lot.financial_item) {
          return this.reservation.lot.financial_item.commission_tiers
        } else if (this.inventory) {
          return this.inventory.commission_tiers
        }
        return '';
      },
      src() {
        if (!this.inventory || !this.inventory.images) {
          return null;
        }

        // Returns first image of the array
        for (let img of this.inventory.images) {
          if (img.data && (img.data.thumb || img.data.original)) {
            return img.data.thumb ? img.data.thumb.url : img.data.original.url;
          }
        }
        return null;
      },
    }
  }
</script>

<style scoped>
  .thumbnail {
    max-height: 5em;
    max-width: 15em;
  }

  .copy-icon {
    cursor: pointer;
    color: #6c757d;
    font-size: 0.9em;
  }

  .copy-icon:hover {
    color: #007bff;
  }
</style>
